import * as Sentry from '@sentry/react';
import { Map } from 'immutable';

import { getIntervalsFromConfig, getPollingInterval, isInvalid, shapeShopsForApp, updateState } from '../utils/shops';
import { createController } from '../utils/controller';
import { createPolling } from '../utils/polling';
import { getSize } from '../utils/iterable/getSize';
// DEV-676 Amplitude events trimming
// import amplitude from '../utils/tracking/amplitude';
import mixpanel from '../utils/tracking/mixpanel';
import api from '../utils/api';

import { POLLING_INTERVAL, STATUSES } from '../constants/shops';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { INITIAL_STATE } from '../constants/store';
import { EMPTY } from '../constants';
import { MODALS } from '../constants/modal';
import { EVENT } from '../constants/tracking';

import actions from '../actions';

const polling = createPolling();
const controller = createController();

function* getShops(reduction, nextShopId) {
  let state = reduction;
  let previousShopId;
  const { signal } = controller.start();
  const interval = state.getIn(['shops', 'pollingInterval']);

  if (
    nextShopId && (
      !state.hasIn(['shops', 'current']) ||
      state.hasIn(['shops', 'byId', nextShopId])
    )
  ) {
    previousShopId = state.setIn(['shops', 'current']);
    state = state.setIn(['shops', 'current'], nextShopId);
  }

  yield function effect(dispatch) {
    function getStatus() {
      dispatch(actions.shops.getShops());
    }

    if (interval) {
      polling.start(setTimeout(getStatus, interval));
    }

    api
      .get({ url: '/shops/status', signal })
      .then(
        (response) => dispatch(actions.shops.getShops.fulfilled({ previousShopId, response })),
        (error) => dispatch(actions.shops.getShops.rejected({ error, signal }))
      );
  };

  return state;
}

function* getShopsFulfilled(reduction, { previousShopId, response }) {
  controller.stop();

  let state = reduction;

  if (state.hasIn(['user', 'userId'])) {
    if (state.getIn(['user', 'userId']) !== response.userId) {
      // got shops of another user, need to reload app
      yield function effect(dispatch) {
        dispatch(actions.user.getUserData());
      };

      return INITIAL_STATE.setIn(['auth', 'loggedIn'], state.getIn(['auth', 'loggedIn']));
    }
  } else {
    state = state.setIn(['user', 'userId'], response.userId);
  }

  let current = state.getIn(['shops', 'current']);
  const intervals = getIntervalsFromConfig(state);
  const { byId, options } = shapeShopsForApp(response);

  if (!getSize(byId)) {
    return state
      .setIn(['shops', 'byId'], EMPTY.MAP)
      .setIn(['shops', 'options'], EMPTY.LIST);
  }

  if (
    (current && !byId.has(current)) ||
    (state.hasIn(['shops', 'options']) && !state.getIn(['shops', 'options']).equals(options))
  ) {
    const interval = intervals.short;

    yield function effect(dispatch) {
      dispatch(actions.user.getSubscriptions());

      function getStatus() {
        dispatch(actions.shops.getShops());
      }

      if (getSize(state.get('billing'))) {
        const shopInManage = state.getIn(['billing', 'manage', 'shopId']);

        if (shopInManage && !byId.has(shopInManage)) {
          dispatch(actions.billing.manageShop());
        }
      }

      polling.start(setTimeout(getStatus, interval));
    };

    if (!byId.has(current)) {
      current = options.get(0);
    }

    return state
      .deleteIn(['user', 'subscriptions'])
      .setIn(['shops', 'byId'], byId)
      .setIn(['shops', 'current'], current)
      .setIn(['shops', 'options'], options)
      .setIn(['shops', 'pollingInterval'], interval);
  } else if (!current) {
    current = options.get(0);
  }

  const currentShop = byId.get(current);
  const currentSyncStatus = currentShop.get('syncStatus');

  if (previousShopId) {
    yield function effect() {
      Sentry.setTag('shopId', current);
      Sentry.setTag('db', currentShop.get('db'));

      // DEV-676 Amplitude events trimming
      // amplitude.logEvent('Switched stores');
      const url = `/shops/${current}/updateShopVisitCount`;
      const payload = { shopId: current, maxShopVisits: 0 };
      const params = { db: currentShop.get('db') };
      api.post({ url, payload, params });
    };

    state = state.deleteIn(['shops', 'waitForSync']);
  }

  // DEV-676 Amplitude events trimming
  // if (
  //   currentSyncStatus !== STATUSES.INITIAL_SYNC &&
  //   state.getIn(['shops', 'byId', current, 'syncStatus']) === STATUSES.INITIAL_SYNC
  // ) {
  //   yield sideEffect(() => {
  //     amplitude.logEvent('Added shop');
  //   });
  // }

  if (
    isInvalid(currentSyncStatus) &&
    !isInvalid(state.getIn(['shops', 'byId', current, 'syncStatus']))
  ) {
    polling.stop();

    state = state
      .setIn(['shops', 'byId'], byId)
      .setIn(['shops', 'current'], current)
      .setIn(['shops', 'options'], options)
      .deleteIn(['shops', 'waitForSync'])
      .deleteIn(['shops', 'syncData', current])
      .deleteIn(['shops', STATUSES.REFRESH_USER, current]);

    state = state
      .deleteIn(['listings', 'placeholders', current])
      .deleteIn(['listings', 'products', 'processing', current]);

    return state;
  }

  const { actions: actionsToDispatch, state: newState } = options.reduce(
    updateState({ current, shopsById: byId }),
    { actions: [], state },
  );

  state = newState;

  if (getSize(actionsToDispatch)) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  if (state.getIn(['shops', 'current']) !== current) {
    state = state.setIn(['shops', 'current'], current);
  }

  if (!byId.equals(state.getIn(['shops', 'byId']))) {
    state = state.setIn(['shops', 'byId'], byId);
  }

  if (!options.equals(state.getIn(['shops', 'options']))) {
    state = state.setIn(['shops', 'options'], options);
  }

  const newInterval = getPollingInterval({ currentShopId: current, intervals, state });

  if (newInterval !== state.getIn(['shops', 'pollingInterval'])) {
    state = state.setIn(['shops', 'pollingInterval'], newInterval);

    yield function effect(dispatch) {
      function getStatus() {
        dispatch(actions.shops.getShops());
      }

      polling.start(setTimeout(getStatus, newInterval));
    };
  }

  return state;
}

function* getShopsRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  controller.stop();
  console.error(error);

  yield function effect(dispatch) {
    dispatch(actions.shops.rescheduleShopsPolling());
  };

  return reduction;
}

function* refreshUserInitiatedShop(reduction, shopId) {
  if (!reduction.hasIn(['shops', 'byId', shopId])) {
    console.error(`Error: Cannot refresh shop, no shop data for shopId "${shopId}"`);
    return reduction;
  }

  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const channel = shop.get('channel');
  const db = shop.get('db');

  yield function effect(dispatch) {
    dispatch(
      actions.shops.setData({
        path: ['syncData', shopId],
        value: Map({ modal: MODALS.SYNC.REFRESH }),
      })
    );

    // DEV-676 Amplitude events trimming
    // amplitude.logEvent('Refresh Shop');

    mixpanel.track(
      EVENT.SHOP.REFRESH,
      {
        ...mixpanel.getChannelAndPlan({ shopId, state: reduction }),
        shop_id: shopId,
      }
    );

    api.shops
      .refresh({ channel, db, shopId })
      .then(
        () => dispatch(actions.shops.getShops()),
        (error) => dispatch(actions.shops.refreshUserInitiatedShop.rejected({ error, shopId })),
      );
  };

  return reduction;
}

function* refreshUserInitiatedShopRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.REFRESH,
        type: NOTIFICATION.ERROR,
      })
    );
  };

  return reduction;
}

function* refreshUserShop(reduction, shopId) {
  yield function effect() {
    api.get({
      url: `/shops/${shopId}/refresh`,
      params: {
        userAction: STATUSES.REFRESH_USER,
        db: reduction.getIn(['shops', 'byId', shopId, 'db']),
      },
    });
  };

  return reduction.setIn(['shops', 'refreshing', shopId], true);
}

function* rescheduleShopsPolling(reduction, interval) {
  polling.stop();

  let state = reduction;
  const newInterval = (
    interval ||
    state.getIn(['shops', 'pollingInterval']) ||
    state.getIn(['user', 'config', 'shopsPollingIntervalLong'], POLLING_INTERVAL.LONG)
  );

  if (newInterval !== state.getIn(['shops', 'pollingInterval'])) {
    state = state.setIn(['shops', 'pollingInterval'], newInterval);
  }

  yield function effect(dispatch) {
    function getStatus() {
      dispatch(actions.shops.getShops());
    }

    polling.start(setTimeout(getStatus, newInterval));
  };

  return state;
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['shops', ...path]
    : ['shops', path];

  if (value) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (fullPath.length > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

function* validateOauthToken(reduction, token) {
  yield function effect(dispatch) {
    api
      .get({ url: '/getOauthFailedToken', params: { token }})
      .then(() => dispatch(actions.shops.setData({ path: 'importFailed', value: true })));
  };

  return reduction;
}

const shops = {
  [actions.shops.getShops]: getShops,
  [actions.shops.getShops.fulfilled]: getShopsFulfilled,
  [actions.shops.getShops.rejected]: getShopsRejected,
  [actions.shops.refreshUserInitiatedShop]: refreshUserInitiatedShop,
  [actions.shops.refreshUserInitiatedShop.rejected]: refreshUserInitiatedShopRejected,
  [actions.shops.refreshUserShop]: refreshUserShop,
  [actions.shops.rescheduleShopsPolling]: rescheduleShopsPolling,
  [actions.shops.setData]: setData,
  [actions.shops.validateOauthToken]: validateOauthToken,
};

export default shops;
